/*
 * tui-cap player engine — plays an exported terminal recording as live DOM
 * text, then turns the transcript pane into a scrollable region.
 *
 * Framework-agnostic and dependency-free. The React wrapper
 * (TuiCapPlayer.tsx) mounts it; the tui-cap GUI uses it directly for previews.
 *
 * Performance notes: one requestAnimationFrame loop drives playback against
 * absolute deadlines; each tick only touches rows whose content changed;
 * each distinct line is built once and cloned thereafter; playback pauses
 * while the player is off-screen or the tab is hidden; the scroll-back view
 * is virtualised for long transcripts. Styles are applied through the CSSOM,
 * so the engine works under a strict Content-Security-Policy.
 */

/** One styled run: start column, style index, text, explicit column width. */
export type TuiCapRun = [number, number, string, number?]
/**
 * A line: its runs; `[baseLine, endCol, tail?]` = another line cut at
 * `endCol` (a line still streaming in) plus optional runs of its own; or
 * `[baseLine, from, to, runs]` = another line with columns [from, to) replaced.
 */
export type TuiCapLine =
  | TuiCapRun[]
  | [number, number]
  | [number, number, TuiCapRun[]]
  | [number, number, number, TuiCapRun[]]

export interface TuiCapData {
  v: 1
  cols: number
  rows: number
  /** Columns used by transcript text (the rest is the scrollbar gutter). */
  contentCols: number
  theme: {bg: string; fg: string; chrome: string; border: string}
  /** [fg, bg, flags] — flags: 1 bold, 2 italic, 4 underline, 8 dim, 16 strike, 32 inverse. */
  styles: Array<[string | null, string | null, number]>
  lines: TuiCapLine[]
  /** Per frame: [holdMs, caretRow, caretCol, row, line, row, line, …]. caretRow -1 = none. */
  frames: number[][]
  /** Transcript pane rows (null when the recording has no transcript pane). */
  pane: {top: number; height: number} | null
  /** Final transcript (line indices) shown in the scroll-back view. */
  doc: number[]
  gutter: {glyph: string; track: string | null; thumb: string | null} | null
  title?: string
  /** Window frame chosen in the editor (null = none). Absent means `'mac'`. */
  chrome?: TuiCapChrome | null
}

/** Window frame styles, matching tui-cap's editor and SVG export. */
export type TuiCapChrome = 'mac' | 'mac-inactive' | 'windows' | 'windows-inactive'

export type TuiCapClass =
  | 'root'
  | 'window'
  | 'chrome'
  | 'dot'
  | 'title'
  | 'screen'
  | 'row'
  | 'pane'
  | 'scroll'
  | 'spacer'
  | 'bar'
  | 'caret'
  | 'replay'
  | 'wide'
  | 'caption'

export interface TuiCapOptions {
  /** `'visible'` (default) starts when at least half the player is on screen. */
  autoPlay?: boolean | 'visible'
  /** Restart automatically after the end (scroll-back is skipped when looping). */
  loop?: boolean
  /** Show a replay button once playback ends (default true). */
  controls?: boolean
  /**
   * Window frame: a style, or `false` for none. By default (or `true`) the
   * recording's style from the editor, else `'mac'`.
   */
  chrome?: boolean | TuiCapChrome
  /** Title bar text (defaults to the recording's title). */
  title?: string
  /** Draw the typing caret (default true). */
  cursor?: boolean
  /** Line height in em (default 1.2). */
  lineHeight?: number
  /** Class names, e.g. a CSS-module object; defaults to `tuicap-<name>`. */
  classes?: Partial<Record<TuiCapClass, string>>
  /** Honour prefers-reduced-motion by showing the final state (default true). */
  respectReducedMotion?: boolean
  onEnd?: () => void
}

export interface TuiCapPlayer {
  play(): void
  pause(): void
  replay(): void
  /** Jump to the end and enable scroll-back. */
  finish(): void
  destroy(): void
  readonly state: 'idle' | 'playing' | 'paused' | 'ended'
}

const FLAG_BOLD = 1
const FLAG_ITALIC = 2
const FLAG_UNDERLINE = 4
const FLAG_DIM = 8
const FLAG_STRIKE = 16
const FLAG_INVERSE = 32

const VIRTUALIZE_ABOVE = 400
const OVERSCAN = 40

function cls(opts: TuiCapOptions, name: TuiCapClass): string {
  return opts.classes?.[name] ?? `tuicap-${name}`
}

/** The window frame to draw ('' = none). */
function chromeOf(opts: TuiCapOptions, data?: TuiCapData): string {
  const c =
    opts.chrome === true ? data?.chrome || 'mac' : (opts.chrome ?? (data?.chrome === undefined ? 'mac' : data.chrome))
  return typeof c === 'string' && /^(mac|windows)(-inactive)?$/.test(c) ? c : ''
}

// Windows 11 caption glyphs (minimize, maximize, close) in 46-unit cells, at 14 units per em.
const CAPTION = 'M18 5h10M64 0h10v10H64zM110 0l10 10m0-10-10 10'

/** Resolve a (possibly prefix-encoded) line into concrete runs. */
function resolveRuns(data: TuiCapData, index: number, memo: Map<number, TuiCapRun[]>): TuiCapRun[] {
  const hit = memo.get(index)
  if (hit) return hit
  const line = data.lines[index] ?? []
  let runs: TuiCapRun[]
  if (typeof line[0] === 'number' && line.length === 4) {
    const [base, from, to, patch] = line as [number, number, number, TuiCapRun[]]
    runs = []
    for (const run of resolveRuns(data, base, memo)) {
      const [col, style, text, width] = run
      const w = width ?? text.length
      if (col + w <= from || col >= to) runs.push(run)
      else if (width === undefined) {
        // Keep the parts of a plain run that fall outside the patched range.
        if (col < from) runs.push([col, style, text.slice(0, from - col)])
        if (col + w > to) runs.push([to, style, text.slice(to - col)])
      }
    }
    runs.push(...patch)
    runs.sort((x, y) => x[0] - y[0])
  } else if (typeof line[0] === 'number') {
    const [base, end, tail] = line as [number, number, TuiCapRun[]?]
    runs = []
    for (const run of resolveRuns(data, base, memo)) {
      const [col, style, text, width] = run
      if (col >= end) break
      const w = width ?? text.length
      if (col + w <= end) runs.push(run)
      else if (width === undefined) runs.push([col, style, text.slice(0, end - col)])
    }
    if (tail) runs.push(...tail)
  } else {
    runs = line as TuiCapRun[]
  }
  memo.set(index, runs)
  return runs
}

type Css = Array<[string, string]>

/** CSS for style `index`, plus its resolved foreground and background. */
function styleOf(data: TuiCapData, index: number): [Css, string | null, string | null] {
  const [fg, bg, f] = data.styles[index] ?? [null, null, 0]
  const inv = f & FLAG_INVERSE
  const color = inv ? (bg ?? data.theme.bg) : fg
  const background = inv ? (fg ?? data.theme.fg) : bg
  const deco = [f & FLAG_UNDERLINE && 'underline', f & FLAG_STRIKE && 'line-through']
  const css = [
    ['color', color],
    ['background', background],
    ['font-weight', f & FLAG_BOLD && '700'],
    ['font-style', f & FLAG_ITALIC && 'italic'],
    ['text-decoration', deco.filter(Boolean).join(' ')],
    ['opacity', f & FLAG_DIM && '.6'],
  ].filter(p => p[1]) as Css
  return [css, color, background]
}

/** True when every code unit is printable ASCII (one column each in any monospace font). */
function isPlain(text: string): boolean {
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) > 0x7e) return false
  return true
}

/** Element access that treats a missing index as 0 (indices are always in range). */
function at(a: ArrayLike<number>, i: number): number {
  return a[i] ?? 0
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** Markup that is already safe to embed as-is. */
class SafeHtml {
  readonly value: string
  constructor(value: string) {
    this.value = value
  }
  toString() {
    return this.value
  }
}

/** Template tag that escapes every interpolated value unless it is `SafeHtml`. */
function html(strings: TemplateStringsArray, ...values: unknown[]): SafeHtml {
  let out = strings[0] ?? ''
  for (const [i, v] of values.entries()) {
    out += (v instanceof SafeHtml ? v.value : escapeHtml(String(v))) + (strings[i + 1] ?? '')
  }
  return new SafeHtml(out)
}

// Block elements U+2580–U+259F as cell rects (x y w h, in eighths), filled like
// a terminal does: font glyphs only cover the em box and leave gaps. The shades
// ░▒▓ (textures, not shapes) keep their glyphs.
const BLOCKS =
  '0084 0781 0682 0583 0484 0385 0286 0187 0088 0078 0068 0058 0048 0038 0028 0018 4048    0081 7018 0444 4444 0044 00484444 00444444 00840444 00844444 4044 40440444 40480444'.split(
    ' ',
  )

/** [blank columns before it, text, pinned (inline-block at its column width), css] */
type Piece = [number, string, boolean, Css]

/**
 * Line `index` split for rendering. Non-ASCII, background and block-element
 * pieces are pinned to their columns and fill the whole row height; blocks are
 * painted as gradients (their text stays, invisible, for copy).
 */
function linePieces(data: TuiCapData, index: number, memo: Map<number, TuiCapRun[]>): Piece[] {
  const out: Piece[] = []
  let col = 0
  for (const [start, style, text, width] of resolveRuns(data, index, memo)) {
    const [css, color, background] = styleOf(data, style)
    let gap = start > col ? start - col : 0
    // A run with an explicit width is one cell (maybe several code units, e.g.
    // █ plus a combining mark): keep it whole, drawn as text.
    const parts =
      width === undefined
        ? (text.match(/([\u2580-\u2590\u2594-\u259f])\1*|[^\u2580-\u2590\u2594-\u259f]+/g) ?? [])
        : [text]
    for (const t of parts) {
      const g = width === undefined && BLOCKS[t.charCodeAt(0) - 0x2580]
      const pinned = !!(g || width !== undefined || !isPlain(t) || background)
      const own: Css = pinned ? [['width', `${width ?? t.length}ch`], ...css] : css
      if (g) {
        const c = color ?? 'var(--tuicap-fg)'
        const layers = (g.match(/..../g) ?? []).map(r => {
          const [x = 0, y = 0, w = 0, h = 0] = [...r].map(d => +d * 12.5)
          return `linear-gradient(90deg,#0000 ${x}%,${c} 0 ${x + w}%,#0000 0) 0 ${(100 * y) / (100 - h) || 0}%/1ch ${h}% repeat-x`
        })
        if (background) layers.push(background)
        own.push(['color', '#0000'], ['background', layers.join()])
      }
      out.push([gap, t, pinned, own])
      gap = 0
    }
    col = start + (width ?? text.length)
  }
  return out
}

/** Screen rows (line indices, -1 = blank) after applying the first `count` frames. */
function rowsAfter(data: TuiCapData, count: number): Int32Array {
  const rows = new Int32Array(data.rows).fill(-1)
  for (let f = 0; f < Math.min(count, data.frames.length); f++) {
    const fr = data.frames[f] ?? []
    for (let k = 3; k + 1 < fr.length; k += 2) rows[at(fr, k)] = at(fr, k + 1)
  }
  return rows
}

/** CSS custom properties that size and colour the terminal. */
export function rootStyle(data: TuiCapData, opts: TuiCapOptions = {}): Record<string, string> {
  return {
    '--tuicap-cols': String(data.cols),
    '--tuicap-rows': String(data.rows),
    '--tuicap-bg': data.theme.bg,
    '--tuicap-fg': data.theme.fg,
    '--tuicap-chrome': data.theme.chrome,
    '--tuicap-border': data.theme.border,
    '--tuicap-lh': String(opts.lineHeight ?? 1.2),
  }
}

/**
 * The player's initial markup as an HTML string — pure, so it is safe during
 * server rendering. `final` renders the last frame (reduced motion / no JS).
 */
export function renderPosterHTML(data: TuiCapData, opts: TuiCapOptions = {}, final = false): string {
  const memo = new Map<number, TuiCapRun[]>()
  const rows = rowsAfter(data, final ? data.frames.length : 1)
  const lineHtml = (index: number): string => {
    let out = ''
    for (const [gap, text, pinned, css] of linePieces(data, index, memo)) {
      // prettier-ignore
      const klass = pinned ? html` class="${cls(opts, 'wide')}"` : ''
      const style = css.map(([k, v]) => `${k}:${v}`).join(';')
      // prettier-ignore
      out += html`${' '.repeat(gap)}<span${klass} style="${style}">${text}</span>`
    }
    return out
  }
  const chrome = chromeOf(opts, data)
  // prettier-ignore
  let out = html`<div class="${cls(opts, 'window')}" data-chrome="${chrome}">`.value
  if (chrome) {
    // prettier-ignore
    const dots = chrome[0] === 'm' ? html`<span class="${cls(opts, 'dot')}"></span>`.value.repeat(3) : ''
    // prettier-ignore
    const caption =
      chrome[0] === 'w'
        ? html`<svg class="${cls(opts, 'caption')}" viewBox="0 0 138 10"><path d="${CAPTION}"/></svg>`
        : ''
    const title = opts.title ?? data.title ?? ''
    // prettier-ignore
    out += html`<div class="${cls(opts, 'chrome')}">${new SafeHtml(dots)}<span class="${cls(opts, 'title')}">${title}</span>${caption}</div>`
  }
  // prettier-ignore
  out += html`<div class="${cls(opts, 'screen')}">`
  for (let y = 0; y < data.rows; y++) {
    // prettier-ignore
    out += html`<div class="${cls(opts, 'row')}">${new SafeHtml(at(rows, y) >= 0 ? lineHtml(at(rows, y)) : '')}</div>`
  }
  return `${out}</div></div>`
}

/** Concrete runs for line `index` (prefixes and patches resolved). */
export function resolveLine(data: TuiCapData, index: number): TuiCapRun[] {
  return resolveRuns(data, index, new Map())
}

/** Size-reserving placeholder markup used before lazily loaded data arrives. */
export function renderPlaceholderHTML(opts: TuiCapOptions = {}): string {
  const chrome = chromeOf(opts)
  // prettier-ignore
  const bar = chrome ? html`<div class="${cls(opts, 'chrome')}"></div>` : ''
  // prettier-ignore
  return html`<div class="${cls(opts, 'window')}" data-chrome="${chrome}">${bar}<div class="${cls(opts, 'screen')}"></div></div>`
    .value
}

/** Mount a player into `root`, replacing any poster markup inside it. */
export function createPlayer(root: HTMLElement, data: TuiCapData, opts: TuiCapOptions = {}): TuiCapPlayer {
  const doc = root.ownerDocument
  const memo = new Map<number, TuiCapRun[]>()
  const templates = new Map<number, DocumentFragment>()
  const lh = opts.lineHeight ?? 1.2
  const pane = data.pane

  for (const [k, v] of Object.entries(rootStyle(data, opts))) root.style.setProperty(k, v)
  root.classList.add(cls(opts, 'root'))

  // Fit the grid to the container exactly: measure the font's real advance
  // (ch/em) unless the host pinned it with --tuicap-advance.
  const pinnedAdvance = root.style.getPropertyValue('--tuicap-advance') !== ''
  const measureAdvance = () => {
    if (pinnedAdvance || !root.isConnected) return
    const probe = doc.createElement('span')
    probe.className = cls(opts, 'window')
    probe.style.cssText = 'position:absolute;visibility:hidden;font-size:100px;border:0;padding:0;white-space:pre'
    probe.textContent = '0'.repeat(50)
    root.append(probe)
    // The measurement needs the probe in the DOM; it runs once on mount.
    // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write
    const advance = probe.getBoundingClientRect().width / 5000
    probe.remove()
    if (advance > 0.3 && advance < 1) root.style.setProperty('--tuicap-advance', advance.toFixed(4))
  }
  measureAdvance()
  const remeasureOnFontsReady = async () => {
    try {
      await doc.fonts?.ready
      measureAdvance()
    } catch {
      // Keep the initial measurement.
    }
  }
  void remeasureOnFontsReady()

  const el = (tag: string, name: TuiCapClass): HTMLElement => {
    const e = doc.createElement(tag)
    e.className = cls(opts, name)
    return e
  }

  const lineFragment = (index: number): DocumentFragment => {
    let tpl = templates.get(index)
    if (!tpl) {
      tpl = doc.createDocumentFragment()
      for (const [gap, text, pinned, css] of linePieces(data, index, memo)) {
        if (gap) tpl.append(' '.repeat(gap))
        const span = doc.createElement('span')
        span.textContent = text
        if (pinned) span.className = cls(opts, 'wide')
        for (const [k, v] of css) span.style.setProperty(k, v)
        tpl.append(span)
      }
      templates.set(index, tpl)
    }
    return tpl.cloneNode(true) as DocumentFragment
  }

  // DOM: [chrome] + screen(rows above the pane, pane, rows below) + caret.
  const win = el('div', 'window')
  const chromeStyle = chromeOf(opts, data)
  win.setAttribute('data-chrome', chromeStyle)
  if (chromeStyle) {
    const chrome = el('div', 'chrome')
    if (chromeStyle[0] === 'm') for (let k = 0; k < 3; k++) chrome.append(el('span', 'dot'))
    const title = el('span', 'title')
    title.textContent = opts.title ?? data.title ?? ''
    chrome.append(title)
    if (chromeStyle[0] === 'w') {
      const ns = 'http://www.w3.org/2000/svg'
      const svg = doc.createElementNS(ns, 'svg')
      const path = doc.createElementNS(ns, 'path')
      svg.setAttribute('class', cls(opts, 'caption'))
      svg.setAttribute('viewBox', '0 0 138 10')
      path.setAttribute('d', CAPTION)
      svg.append(path)
      chrome.append(svg)
    }
    win.append(chrome)
  }
  const screen = el('div', 'screen')
  const rowEls: HTMLElement[] = []
  const paneEl = pane ? el('div', 'pane') : null
  if (paneEl && pane) paneEl.style.height = `${pane.height * lh}em`
  for (let y = 0; y < data.rows; y++) {
    const row = el('div', 'row')
    rowEls.push(row)
    if (paneEl && pane && y === pane.top) screen.append(paneEl)
    if (paneEl && pane && y >= pane.top && y < pane.top + pane.height) paneEl.append(row)
    else screen.append(row)
  }
  const caret = el('div', 'caret')
  caret.hidden = true
  screen.append(caret)
  win.append(screen)
  let replayBtn: HTMLButtonElement | null = null
  if (opts.controls !== false) {
    replayBtn = el('button', 'replay') as HTMLButtonElement
    replayBtn.type = 'button'
    replayBtn.textContent = '\u21bb Replay'
    replayBtn.hidden = true
    win.append(replayBtn)
  }
  root.replaceChildren(win)
  if (!root.hasAttribute('role')) root.setAttribute('role', 'region')
  if (!root.hasAttribute('aria-label')) root.setAttribute('aria-label', 'Terminal recording')

  // Playback state. `ends[i]` is the absolute deadline (ms) when frame i ends.
  const shown = new Int32Array(data.rows).fill(-2)
  const target = new Int32Array(data.rows).fill(-1)
  const ends = new Float64Array(data.frames.length)
  let total = 0
  for (let i = 0; i < data.frames.length; i++) {
    total += Math.max(0, at(data.frames[i] ?? [], 0))
    ends[i] = total
  }
  let frame = -1
  let state: TuiCapPlayer['state'] = 'idle'
  let startAt = 0
  let pausedAt = 0
  let raf = 0
  let visible = true
  let resumeOnShow = false
  let scrollMode = false
  let caretRow = -1
  let caretCol = 0

  const applyFrame = (i: number) => {
    const fr = data.frames[i] ?? []
    caretRow = at(fr, 1)
    caretCol = at(fr, 2)
    for (let k = 3; k + 1 < fr.length; k += 2) target[at(fr, k)] = at(fr, k + 1)
  }

  const paint = () => {
    for (let y = 0; y < data.rows; y++) {
      const line = at(target, y)
      if (shown[y] === line) continue
      shown[y] = line
      if (line < 0) rowEls[y]?.replaceChildren()
      else rowEls[y]?.replaceChildren(lineFragment(line))
    }
    if (opts.cursor !== false && caretRow >= 0 && !scrollMode) {
      caret.hidden = false
      caret.style.top = `${caretRow * lh}em`
      caret.style.left = `${caretCol}ch`
    } else {
      caret.hidden = true
    }
  }

  const seek = (i: number) => {
    target.fill(-1)
    for (let f = 0; f <= i && f < data.frames.length; f++) applyFrame(f)
    frame = i
    paint()
  }

  // Scroll-back: the pane becomes a (virtualised) scroll container showing
  // the whole final transcript, with the CLI's scrollbar drawn beside it.
  let scrollEl: HTMLElement | null = null
  let barEl: HTMLElement | null = null
  let spacer: HTMLElement | null = null
  let rendered: [number, number] = [-1, -1]
  let scrollRaf = 0

  const drawBar = () => {
    if (!scrollEl || !barEl || !pane || !data.gutter) return
    const count = data.doc.length
    const vis = pane.height
    if (count <= vis) {
      barEl.replaceChildren()
      return
    }
    const thumb = Math.max(1, Math.round((vis / count) * vis))
    const max = scrollEl.scrollHeight - scrollEl.clientHeight
    const pos = Math.round((max > 0 ? scrollEl.scrollTop / max : 1) * (vis - thumb))
    const frag = doc.createDocumentFragment()
    for (let k = 0; k < vis; k++) {
      const g = doc.createElement('div')
      g.textContent = data.gutter.glyph
      const color = k >= pos && k < pos + thumb ? data.gutter.thumb : data.gutter.track
      if (color) g.style.color = color
      frag.append(g)
    }
    barEl.replaceChildren(frag)
  }

  const renderWindow = () => {
    if (!scrollEl || !spacer) return
    const n = data.doc.length
    const rh = rowPx()
    const virtual = n > VIRTUALIZE_ABOVE
    const first = virtual ? Math.max(0, Math.floor(scrollEl.scrollTop / rh) - OVERSCAN) : 0
    const last = virtual ? Math.min(n, Math.ceil((scrollEl.scrollTop + scrollEl.clientHeight) / rh) + OVERSCAN) : n
    if (rendered[0] !== first || rendered[1] !== last) {
      rendered = [first, last]
      const frag = doc.createDocumentFragment()
      for (let r = first; r < last; r++) {
        const row = el('div', 'row')
        row.append(lineFragment(at(data.doc, r)))
        frag.append(row)
      }
      spacer.style.paddingTop = `${first * lh}em`
      spacer.style.paddingBottom = `${(n - last) * lh}em`
      spacer.replaceChildren(frag)
    }
    drawBar()
  }

  // The top line and row height as of the last scroll: when the player resizes
  // (the font scales with the container), the same line is put back on top.
  let lineAt = 0
  let rhAt = 0
  const onScroll = () => {
    if (scrollRaf) return
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0
      const rh = rowPx()
      if (rhAt && Math.abs(rh - rhAt) > 0.01) toLine(lineAt, rh)
      rhAt = rh
      lineAt = topLine(rh)
      renderWindow()
    })
  }

  // Terminal-style scrolling: the view moves in whole lines, never between
  // them. Wheel and keys step instantly (wheel pixels accumulate into lines,
  // covering the same distance as native scrolling); touch momentum comes to
  // rest on a line through CSS scroll snapping. Like a terminal window, a
  // wheel anywhere over the player scrolls the transcript and never the page
  // (unless the transcript fits without scrolling).
  let wheelAcc = 0
  const page = pane?.height ?? 1
  const rowPx = (): number => (scrollEl ? parseFloat(getComputedStyle(scrollEl).fontSize) * lh : 0) || 16
  const topLine = (rh: number) => Math.round((scrollEl?.scrollTop ?? 0) / rh)
  const toLine = (line: number, rh: number) => {
    // Scrolling to a whole line must read the scroller's current geometry.
    // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write
    if (scrollEl) scrollEl.scrollTop = Math.max(0, Math.min(data.doc.length - page, line)) * rh
  }

  const onWheel = (e: WheelEvent) => {
    const dy = e.deltaY
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.ctrlKey || Math.abs(e.deltaX) > Math.abs(dy) || data.doc.length <= page) return
    const rh = rowPx()
    // At either end the page scrolls instead of the (unmovable) transcript.
    const atStart = dy < 0 && topLine(rh) <= 0
    const atEnd = dy > 0 && topLine(rh) >= data.doc.length - page
    if (atStart || atEnd) {
      wheelAcc = 0
      return
    }
    e.preventDefault()
    const lines = dy * (e.deltaMode === 1 ? 1 : e.deltaMode === 2 ? page : 1 / rh)
    if (Math.sign(lines) !== Math.sign(wheelAcc)) wheelAcc = 0
    wheelAcc += lines
    const step = Math.trunc(wheelAcc)
    wheelAcc -= step
    if (step) toLine(topLine(rh) + step, rh)
  }

  // Terminal-style scrolling keys on the focused transcript only (not app shortcuts),
  // so they stay local rather than going through @github-ui/ui-commands.
  const onKey = (e: KeyboardEvent) => {
    const {key, shiftKey, altKey, ctrlKey, metaKey} = e
    const steps: Record<string, number> = {
      ArrowDown: 1,
      ArrowUp: -1,
      PageDown: page,
      PageUp: -page,
      ' ': shiftKey ? -page : page,
      Home: -1e9,
      End: 1e9,
    }
    const d = steps[key]
    if (!d || altKey || ctrlKey || metaKey) return
    e.preventDefault()
    const rh = rowPx()
    toLine(topLine(rh) + d, rh)
  }

  const enterScroll = () => {
    if (!pane || !paneEl || data.doc.length === 0 || scrollMode) return
    scrollMode = true
    caret.hidden = true
    scrollEl = el('div', 'scroll')
    scrollEl.tabIndex = 0
    scrollEl.setAttribute('role', 'region')
    scrollEl.setAttribute('aria-label', 'Transcript (scrollable)')
    spacer = el('div', 'spacer')
    scrollEl.append(spacer)
    paneEl.replaceChildren(scrollEl)
    if (data.gutter) {
      barEl = el('div', 'bar')
      barEl.style.left = `${data.cols - 1}ch`
      paneEl.append(barEl)
    }
    rendered = [-1, -1]
    wheelAcc = 0
    // The virtualised window and scrollbar track the exact scroll offset, which observers don't report.
    // eslint-disable-next-line github/prefer-observers
    scrollEl.addEventListener('scroll', onScroll)
    // Not passive: onWheel calls preventDefault() so the page doesn't scroll along with the transcript.
    // eslint-disable-next-line github/require-passive-events
    win.addEventListener('wheel', onWheel, {passive: false})
    scrollEl.addEventListener('keydown', onKey)
    renderWindow()
    // Start at the end of the transcript; runs once when playback ends.
    // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write
    scrollEl.scrollTop = scrollEl.scrollHeight
    renderWindow()
    rhAt = rowPx()
    lineAt = topLine(rhAt)
  }

  const leaveScroll = () => {
    if (!scrollMode || !paneEl || !pane) return
    scrollMode = false
    scrollEl?.removeEventListener('scroll', onScroll)
    win.removeEventListener('wheel', onWheel)
    scrollEl?.removeEventListener('keydown', onKey)
    if (scrollRaf) cancelAnimationFrame(scrollRaf)
    scrollRaf = 0
    scrollEl = null
    barEl = null
    spacer = null
    paneEl.replaceChildren(...rowEls.slice(pane.top, pane.top + pane.height))
  }

  const stopLoop = () => {
    if (raf) cancelAnimationFrame(raf)
    raf = 0
  }

  const schedule = () => {
    if (!raf && state === 'playing' && visible && !doc.hidden) raf = requestAnimationFrame(tick)
  }

  function tick(now: number): void {
    raf = 0
    if (state !== 'playing') return
    const elapsed = now - startAt
    let advanced = false
    while (frame + 1 < data.frames.length && (frame < 0 || at(ends, frame) <= elapsed)) {
      frame++
      applyFrame(frame)
      advanced = true
    }
    if (advanced) paint()
    if (frame >= data.frames.length - 1 && elapsed >= total) {
      end()
      return
    }
    schedule()
  }

  function end(): void {
    stopLoop()
    state = 'ended'
    root.removeAttribute('aria-busy')
    if (opts.loop && !reduced) {
      api.replay()
      return
    }
    enterScroll()
    paint()
    if (replayBtn) replayBtn.hidden = false
    opts.onEnd?.()
  }

  const api: TuiCapPlayer = {
    get state() {
      return state
    },
    play() {
      if (state === 'playing' || data.frames.length === 0) return
      if (state === 'ended') {
        api.replay()
        return
      }
      const now = performance.now()
      startAt = state === 'paused' ? now - pausedAt : now
      state = 'playing'
      if (replayBtn && doc.activeElement !== replayBtn) replayBtn.hidden = true
      schedule()
    },
    pause() {
      if (state !== 'playing') return
      pausedAt = performance.now() - startAt
      state = 'paused'
      stopLoop()
    },
    replay() {
      stopLoop()
      leaveScroll()
      state = 'idle'
      seek(0)
      api.play()
    },
    finish() {
      stopLoop()
      leaveScroll()
      seek(data.frames.length - 1)
      end()
    },
    destroy() {
      stopLoop()
      io?.disconnect()
      doc.removeEventListener('visibilitychange', onVisibility)
      leaveScroll()
      state = 'paused'
    },
  }
  replayBtn?.addEventListener('click', () => api.replay())

  // Pause while the tab is hidden; resume when it comes back.
  const onVisibility = () => {
    if (doc.hidden) {
      if (state === 'playing') {
        api.pause()
        resumeOnShow = true
      }
    } else if (resumeOnShow && visible) {
      resumeOnShow = false
      api.play()
    }
  }
  doc.addEventListener('visibilitychange', onVisibility)

  const reduced =
    opts.respectReducedMotion !== false &&
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches

  seek(0)
  let io: IntersectionObserver | null = null
  if (reduced) {
    api.finish()
  } else if (opts.autoPlay === 'visible' || opts.autoPlay === undefined) {
    if (typeof IntersectionObserver === 'function') {
      // Start (and resume) only while at least half the player is visible.
      io = new IntersectionObserver(
        entries => {
          const on = entries.some(e => e.isIntersecting && e.intersectionRatio >= 0.5)
          visible = on
          if (on && (state === 'idle' || (state === 'paused' && resumeOnShow))) {
            resumeOnShow = false
            api.play()
          } else if (!on && state === 'playing') {
            api.pause()
            resumeOnShow = true
          }
        },
        {threshold: [0, 0.5]},
      )
      io.observe(root)
    } else {
      api.play()
    }
  } else if (opts.autoPlay === true) {
    api.play()
  }
  return api
}

/** Player stylesheet. Class names are `tuicap-*`; the React wrapper may map them. */
export const TUICAP_CSS = `
.tuicap-root{container-type:inline-size;display:block;width:100%}
.tuicap-window{position:relative;box-sizing:border-box;font-family:var(--tuicap-font,var(--custom-fontStack-monospace,ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace));font-size:calc(100cqw / (var(--tuicap-cols) * var(--tuicap-advance,0.6) + 2.2));line-height:calc(var(--tuicap-lh) * 1em);background:var(--tuicap-bg);color:var(--tuicap-fg);border:1px solid var(--tuicap-border);border-radius:.6em;overflow:hidden;font-variant-ligatures:none;font-kerning:none;text-rendering:optimizeSpeed}
.tuicap-chrome{display:flex;align-items:center;gap:.45em;height:2.4em;padding:0 .9em;background:var(--tuicap-chrome)}
.tuicap-dot{width:.8em;height:.8em;border-radius:50%;background:#ff5f57}
.tuicap-dot:nth-child(2){background:#febc2e}
.tuicap-dot:nth-child(3){background:#28c840}
.tuicap-title{flex:1;text-align:center;margin-right:3.5em;font-size:.9em;opacity:.8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tuicap-screen{position:relative;padding:1em;width:calc(var(--tuicap-cols) * 1ch);height:calc(var(--tuicap-rows) * var(--tuicap-lh) * 1em);box-sizing:content-box;contain:layout paint}
.tuicap-row{height:calc(var(--tuicap-lh) * 1em);white-space:pre;overflow:hidden}
.tuicap-wide{display:inline-block;overflow:visible;vertical-align:top}
.tuicap-pane{position:relative;overflow:hidden}
.tuicap-scroll{height:100%;overflow-y:auto;scrollbar-width:none;scroll-snap-type:y mandatory;scroll-behavior:auto}
.tuicap-scroll:focus-visible{outline:2px solid currentColor;outline-offset:-2px}
.tuicap-scroll .tuicap-row{scroll-snap-align:start}
.tuicap-scroll::-webkit-scrollbar{display:none}
.tuicap-bar{position:absolute;top:0;width:1ch;pointer-events:none;line-height:calc(var(--tuicap-lh) * 1em)}
.tuicap-caret{position:absolute;margin:1em 0 0 1em;width:1ch;height:calc(var(--tuicap-lh) * 1em);background:var(--tuicap-fg);opacity:.85;pointer-events:none}
.tuicap-replay{position:absolute;right:.8em;bottom:.8em;font:inherit;font-size:max(.85em,.875rem);padding:.3em .7em;border-radius:.4em;border:1px solid var(--tuicap-border);background:var(--tuicap-chrome);color:var(--tuicap-fg);cursor:pointer}
.tuicap-replay[hidden],.tuicap-caret[hidden]{display:none}
[data-chrome=mac-inactive] .tuicap-dot{background:#464a4c}
.tuicap-window[data-chrome^=w]{border-radius:.25em;border-color:color-mix(in srgb,var(--tuicap-border) 40%,transparent)}
[data-chrome^=w] .tuicap-chrome{height:1.9em;padding:0 0 0 1em}
[data-chrome^=w] .tuicap-title{text-align:left;margin:0;opacity:1;font:.85em system-ui,sans-serif}
[data-chrome=windows-inactive] .tuicap-chrome>*{opacity:.36}
.tuicap-caption{flex:none;width:9.86em;height:.71em;overflow:visible;fill:none;stroke:currentColor}
`
